import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { takeUntil, finalize, first } from 'rxjs/operators';
import { WorkerAgreementDetailsDto } from '../../models/dtos/worker-agreement-details-dto';
import { EmploymentAgreementDetailsDto } from '../../models/dtos/employment-agreement-details-dto';
import { EmploymentType } from '../../models/enums/employment-type-enum';
import { WorkerAgreementService } from '../../data/worker-agreement.service';
import { EmploymentAgreementService } from '../../data/employment-agreement.service';
import { SplitComponentService } from '../../split/services/split-component.service';
import { SplitComponentEnum } from '../../common/enums/split-components';
import { AgreementAnnexSidePanelComponent } from './agreement-annex-side-panel/agreement-annex-side-panel.component';
import { AnnexProcessStep } from 'src/app/common/enums/annex-process-step';
import { AnnexBasicDataStepModel } from 'src/app/models/dtos/annexes/annex-basic-data-step-model';
import { AnnexService } from 'src/app/data/annex.service';
import { AnnexPreviewService } from '../services/annex-preview.service';
import { GenerateAnnexPreviewRequest } from 'src/app/contracts/requests/annexes/generate-annex-preview-request';
import { AnnexChangedDataStepModel } from 'src/app/models/dtos/annexes/annex-changed-data-step-model';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { BasicDataStepComponent } from './basic-data-step/basic-data-step.component';
import { ChangedDataStepComponent } from './changed-data-step/changed-data-step.component';
import { WorkerAgreementForAnnexDto } from 'src/app/models/dtos/worker-agreements/worker-agreement-for-annex-dto';
import { CreateAnnexRequest } from 'src/app/contracts/requests/annexes/create-annex-request';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ConflictedAnnexesDto } from 'src/app/models/dtos/annexes/conflicted-annexes-dto';
import { ConflictedAnnexDialogComponent, CurrentChangesEntry } from './conflicted-annex-dialog/conflicted-annex-dialog-component';

@Component({
  selector: 'app-agreement-annex',
  templateUrl: './agreement-annex.component.html',
  styleUrls: ['./agreement-annex.component.scss'],
  providers: [AnnexPreviewService],
  standalone: false,
})
export class AgreementAnnexComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private splitComponentService = inject(SplitComponentService);
  private workerAgreementService = inject(WorkerAgreementService);
  private employmentAgreementService = inject(EmploymentAgreementService);
  private spinner = inject(NgxSpinnerService);
  private annexService = inject(AnnexService);
  private annexPreviewService = inject(AnnexPreviewService);
  private snackbarService = inject(SnackBarService);
  private dialog = inject(MatDialog);

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild(BasicDataStepComponent) basicStep!: BasicDataStepComponent;
  @ViewChild(ChangedDataStepComponent) changedStep!: ChangedDataStepComponent;

  agreementId: number;
  workerId: number;
  employmentTypeId: number;
  agreement: WorkerAgreementDetailsDto | EmploymentAgreementDetailsDto;
  splitComponentId = 'agreement-annex-split';
  annexProcessStep = AnnexProcessStep;
  dataForAnnex: WorkerAgreementForAnnexDto;
  previewError: boolean;
  isGenerating = false;

  private destroy$ = new Subject<void>();

  private basicDataFormValue: AnnexBasicDataStepModel = null;
  private changedDataFormValue: AnnexChangedDataStepModel = null;

  selectedTabIndex = 0;

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.agreementId = +params['agreementId'];
      this.workerId = +params['workerId'];
      this.employmentTypeId = +params['employmentTypeId'];
      this.canCreate();
      this.loadAgreementDetails();
    });
  }

  ngOnDestroy(): void {
    this.splitComponentService.destroySplitter(this.splitComponentId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private canCreate() {
    this.spinner.show();
    this.annexService
      .canCreateAnnex(this.agreementId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => {},
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(error.error);
          this.router.navigate(['/workerAgreements']);
        },
      });
  }

  private loadAgreementDetails(): void {
    this.spinner.show();

    if (this.isEmploymentAgreement()) {
      forkJoin({
        agreement: this.employmentAgreementService.getEmploymentAgreementDetails(this.agreementId),
        dataForAnnex: this.workerAgreementService.getWorkerAgreementForAnnex(this.agreementId),
      })
        .pipe(
          first(),
          finalize(() => this.spinner.hide()),
        )
        .subscribe({
          next: ({ agreement, dataForAnnex }) => {
            this.agreement = agreement;
            this.dataForAnnex = dataForAnnex;
            this.showAgreementDetails();
            this.generatePreview(null, false, false);
          },
          error: () => {
            this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
          },
        });
    } else {
      forkJoin({
        agreement: this.workerAgreementService.getWorkerAgreementDetails(this.agreementId),
        dataForAnnex: this.workerAgreementService.getWorkerAgreementForAnnex(this.agreementId),
      })
        .pipe(
          first(),
          finalize(() => this.spinner.hide()),
        )
        .subscribe({
          next: ({ agreement, dataForAnnex }) => {
            this.agreement = agreement;
            this.dataForAnnex = dataForAnnex;
            this.showAgreementDetails();
            this.generatePreview(null, false, false);
          },
          error: () => {
            this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
          },
        });
    }
  }

  onBackClick(): void {
    this.stepper.previous();
  }

  onNextClick(): void {
    if (this.isStepCompleted(this.stepper.selectedIndex)) {
      this.stepper.next();
    }
  }

  isStepCompleted(stepIndex: AnnexProcessStep): boolean {
    switch (stepIndex + 1) {
      case AnnexProcessStep.BasicData:
        return this.isBasicDataValid();
      case AnnexProcessStep.ChangedData:
        return this.isChangedDataValid();
      case AnnexProcessStep.Summary:
        return false;
      default:
        return false;
    }
  }

  goToStep(stepIndex: number): void {
    this.stepper.selectedIndex = stepIndex;
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  showAgreementDetails(): void {
    const existingComponents = this.splitComponentService.getComponentsConfigs(this.splitComponentId);
    const detailsAlreadyShown = existingComponents?.some((component) => component.id === SplitComponentEnum.AgreementAnnexDetails);

    if (!detailsAlreadyShown) {
      this.splitComponentService.addComponent(this.splitComponentId, {
        id: SplitComponentEnum.AgreementAnnexDetails,
        componentType: AgreementAnnexSidePanelComponent,
        direction: 'horizontal',
        data: {
          agreement: this.agreement,
          splitId: this.splitComponentId,
          annexPreviewService: this.annexPreviewService,
        },
        size: 33,
        header: {
          closeButton: false,
        },
      });
    }
  }


  async onGenerateAnnexClick(): Promise<void> {
    if (this.previewError) {
      return;
    }

    if (!this.canGenerateAnnex) {
      return;
    }

    this.isGenerating = true;
    const createAnnexRequest = this.createAnnexRequest(false);

    try {
      this.spinner.show();
      const annexId = await firstValueFrom(this.annexService.createAnnex(this.agreement.AgreementId, createAnnexRequest));
      this.spinner.hide();
      this.onAnnexGenerated(annexId);
    } catch (error: any) {
      this.spinner.hide();
      this.isGenerating = false;
      if (error.status === 409 && error.error === ErrorCode.FoundConflictedAnnexes) {
        await this.handleConflictError();
      } else {
        this.snackbarService.openErrorSnackBar(error.error);
      }
    }
  }

  async handleConflictError(): Promise<void> {
    try {
      const conflictsData = await firstValueFrom(
        this.annexService.getConflictedAnnexes(
          this.agreement.AgreementId,
          this.changedDataFormValue.ChangesWage,
          this.changedDataFormValue.ChangesJobTitle,
          this.changedDataFormValue.ChangesTemporaryWorkplace,
          this.changedDataFormValue.ChangesSupervisor,
          this.changedDataFormValue.ChangesWorkingTimeGroup,
          moment(this.basicDataFormValue.AnnexStartDate),
          this.basicDataFormValue.AnnexEndDate ? moment(this.basicDataFormValue.AnnexEndDate) : null,
        ),
      );
      const confirmed = await this.showConflictDialog(conflictsData);

      if (!confirmed) {
        return;
      }
      this.spinner.show();

      const createAnnexRequest = this.createAnnexRequest(true);
      const annexId = await firstValueFrom(this.annexService.createAnnex(this.agreement.AgreementId, createAnnexRequest));
      this.spinner.hide();
      this.onAnnexGenerated(annexId);
    } catch (error: any) {
      this.spinner.hide();
      this.snackbarService.openErrorSnackBar(error.error);
    }
  }

  async showConflictDialog(conflicts: ConflictedAnnexesDto[]): Promise<boolean> {
    const currentChanges: CurrentChangesEntry = {
      ChangesWage: this.changedDataFormValue.ChangesWage,
      ChangesJobTitle: this.changedDataFormValue.ChangesJobTitle,
      ChangesSupervisor: this.changedDataFormValue.ChangesSupervisor,
      ChangesTemporaryWorkplace: this.changedDataFormValue.ChangesTemporaryWorkplace,
      ChangesWorkingTimeGroup: this.changedDataFormValue.ChangesWorkingTimeGroup,
      AnnexStartDate: this.basicDataFormValue.AnnexStartDate,
      AnnexEndDate: this.basicDataFormValue.AnnexEndDate,
    };

    const dialogRef = this.dialog.open(ConflictedAnnexDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        conflicts: conflicts,
        currentChanges: currentChanges,
      },
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  onAnnexGenerated(annexId: number) {
    this.router.navigate(['successfullyCreatedAnnex', annexId], {
      queryParams: {
        workerId: this.workerId,
        employmentTypeId: this.employmentTypeId,
        agreementId: this.agreementId,
      },
    });
  }

  get canGoNext(): boolean {
    switch (this.stepper?.selectedIndex) {
      case 0:
        return this.isBasicDataValid();
      case 1:
        return this.isChangedDataValid();
      default:
        return false;
    }
  }

  get isLastStep(): boolean {
    return this.stepper?.selectedIndex === AnnexProcessStep.Summary - 1;
  }

  get canGenerateAnnex(): boolean {
    return this.isLastStep && this.isBasicDataValid() && this.isChangedDataValid();
  }

  get annexPreviewData(): GenerateAnnexPreviewRequest {
    return {
      ...this.basicDataFormValue,
      ...this.changedDataFormValue,
      Wages: this.changedDataFormValue?.Wages?.map((wage) => ({
        Value: wage.Value,
        CurrencyName: wage.CurrencyName,
        WageTypeName: wage.WageTypeName,
        CalculationDescription: wage.CalculationDescription,
      })),
    };
  }

  onBasicDataChange(data: AnnexBasicDataStepModel): void {
    this.basicDataFormValue = data;
    this.generatePreview(null, true, false);
  }

  onChangedDataChange(event: { data: AnnexChangedDataStepModel; sectionKey: string }): void {
    this.changedDataFormValue = event.data;
    this.generatePreview(event.sectionKey, false, true);
  }

  retryGeneratePreview(): void {
    this.generatePreview(null, false, false);
  }

  private createAnnexRequest(conflictsAccepted: boolean): CreateAnnexRequest {
    return {
      ...this.basicDataFormValue,
      ...this.changedDataFormValue,
      Wages: this.changedDataFormValue?.Wages?.map((wage) => ({
        Value: wage.Value,
        WageTypeId: wage.WageTypeId,
        CurrencyId: wage.CurrencyId,
        CalculationDescription: wage.CalculationDescription,
        WageDescriptionId: wage.WageDescriptionId
      })),
      ConflictsAccepted: conflictsAccepted,
      PreviousData: {
        JobTitleId: this.dataForAnnex.JobTitleId,
        OrganizationalUnitId: this.dataForAnnex.OrganizationalUnitId,
        PotentialSupervisorId: this.dataForAnnex.PotentialSupervisorId,
        TemporaryWorkplaceId: this.dataForAnnex.TemporaryWorkplaceId,
        TemporaryWorkplaceName: this.dataForAnnex.TemporaryWorkplaceName,
        WorkingTimeGroupId: this.dataForAnnex.WorkingTimeGroupId,
        Wages: this.dataForAnnex.Wages.map((wage) => ({
          Value: wage.Value,
          WageTypeId: wage.WageTypeId,
          CurrencyId: wage.CurrencyId,
          CalculationDescription: wage.CalculationDescription,
          WageDescriptionId: wage.WageDescriptionId
        })),
      },
    };
  }

  private generatePreview(sectionKey: string, basic: boolean, changed: boolean): void {
    const previewRequest: GenerateAnnexPreviewRequest = {
      ...this.annexPreviewData,
    };

    this.annexService
      .generateAnnexPreview(this.agreement.AgreementId, previewRequest)
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (blob) {
            this.annexPreviewService.updatePreview(blob);
            this.previewError = false;
            if (sectionKey && changed) {
              this.changedStep.showSuccess(sectionKey);
            } else if (basic) {
              this.basicStep.showSuccess();
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(ErrorCode.AnnexPreviewGenerationError);
          this.previewError = true;
          if (sectionKey && changed) {
            this.changedStep.showError(sectionKey);
          } else if (basic) {
            this.basicStep.showError();
          }
        },
      });
  }

  private isBasicDataValid(): boolean {
    return this.basicStep?.basicDataForm.valid;
  }

  private isChangedDataValid(): boolean {
    return this.changedStep?.changedDataForm.valid && this.changedStep?.hasAnyChanges;
  }

  private isEmploymentAgreement(): boolean {
    return this.employmentTypeId === EmploymentType.EmploymentAgreement || this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement;
  }

  private isWorkerAgreement(): boolean {
    return this.employmentTypeId === EmploymentType.MandateAgreement || this.employmentTypeId === EmploymentType.TemporaryMandateAgreement;
  }

  get workerAgreement(): WorkerAgreementDetailsDto | null {
    return this.isWorkerAgreement() ? (this.agreement as WorkerAgreementDetailsDto) : null;
  }

  get employmentAgreement(): EmploymentAgreementDetailsDto | null {
    return this.isEmploymentAgreement() ? (this.agreement as EmploymentAgreementDetailsDto) : null;
  }
}
